import React from 'react';

/**
 * The tab strip for the User Feedback screen (feature 067-user-feedback-tabs).
 *
 * Presentational only — it knows the tab list, which one is active and how to say "select that
 * one". Which body renders, and when it mounts, is UserFeedback.js's business.
 *
 * ACCESSIBILITY IS NOT DECORATION HERE. A tab strip built from plain <div>s is invisible to a
 * screen reader as a control: `role="tablist"` / `role="tab"` / `aria-selected` are what make
 * "there are four sources and you are looking at the third" perceivable at all. They are also
 * how the tests address the tabs, so a regression in the semantics fails the suite.
 *
 * `second-day` (Second Day Feedback, 062) was REMOVED from this list by request. Removing it here
 * is the whole of it: `resolveTab` now treats the id as unknown, so an old `?tab=second-day`
 * bookmark opens the default tab instead of erroring, and `UserFeedback` never mounts its body.
 */
/*
 * The `id`s are URL contract (`?tab=`) and are NOT renamed with the labels — an admin's bookmark
 * or a link in a chat has to keep opening the tab it opened before. Only what a person reads
 * changes here.
 */
export const TABS = [
  { id: 'firebase', label: 'In-App Feedback Section' },
  { id: 'survey', label: 'Survey Responses' },
  { id: 'signup-survey', label: '15 Day Trial Feedback' },
  { id: 'ceo', label: 'CEO Requests' },
];

export const DEFAULT_TAB = TABS[0].id;

/** Unknown or missing → the default. A bad URL must never produce an error (FR-005). */
export function resolveTab(id) {
  return TABS.some((t) => t.id === id) ? id : DEFAULT_TAB;
}

export default function FeedbackTabs({ active, onSelect }) {
  return (
    <div className="uf-tabs" role="tablist" aria-label="Feedback sources">
      {TABS.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`uf-tab-${t.id}`}
            aria-selected={isActive}
            aria-controls={`uf-panel-${t.id}`}
            className={`uf-tab${isActive ? ' uf-tab--active' : ''}`}
            onClick={() => onSelect(t.id)}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
